import React, { useLayoutEffect, useState } from "react";
import { MosaicWindow } from "src/components/Mosaic/MosaicWindow";
import SplitterLayout from "react-splitter-layout";
import "react-splitter-layout/lib/index.css";
import { Grid } from "@mui/material";
import classNames from "classnames";

// Redux
import { useDispatch, useSelector } from "react-redux";
import * as MosaicManagerActions from "src/redux/actions/Mosaic.Actions";
import { IMosaicNode, isLeafNode, IMosaicTabNode, SplitDirection } from "src/utils/MosaicTree";
import { Reparentable } from "src/components/Reparentable";
import { MosaicTabPanel } from "src/components/Mosaic/MosaicTabPanel";
import { IRootState } from "src/redux/reducers/CombinedReducers";
interface IPanelSizes {
  [path: string]: number;
}

interface IPanelNode {
  node: IMosaicNode;
  path: string[];
  splitPercentage: number;
}

// Set minimum allowable mosaic panel size (in percentage);
const minMosaicPanelSize = 15;

function renderPanels(rootNode: IMosaicNode, path: string[]): any {
  const panels = [];
  const visitedIds: { [key: string]: any } = {};
  const queue: IPanelNode[] = [{ node: rootNode, path: [], splitPercentage: 50 }];
  while (queue.length > 0) {
    const { node, path, splitPercentage } = queue.pop();
    if (!visitedIds[node.id]) {
      visitedIds[node.id] = true;
      if (isLeafNode(node)) {
        const activeIndex = node.activeIndex;
        const numOpenTabs = node.tabNodes.length;
        panels.push(
          ...node.tabNodes.map((tabItem: IMosaicTabNode, index: number) => (
            <Reparentable key={tabItem.id} uid={tabItem.id} path={path}>
              <MosaicTabPanel
                isHidden={index !== activeIndex}
                tabItem={tabItem}
                numTabsOpen={numOpenTabs}
                path={path}
                splitPercentage={splitPercentage}
              />
            </Reparentable>
          ))
        );
      } else {
        queue.push({
          node: node.first,
          path: [...path, "first"],
          splitPercentage: node.splitPercentage,
        });
        queue.push({
          node: node.second,
          path: [...path, "second"],
          splitPercentage: node.splitPercentage,
        });
      }
    }
  }
  return panels;
}

function renderWindows(node: IMosaicNode, path: string[], onSplitterDragEnd: any, updatePanelSize: any): JSX.Element {
  if (!node) {
    return null;
  }
  if (isLeafNode(node)) {
    return <MosaicWindow leafNode={node} path={path} />;
  } else {
    const percent = node.splitPercentage ? node.splitPercentage : 50;
    const isVertical = node.direction === SplitDirection.Vertical;
    return (
      <SplitterLayout
        vertical={isVertical}
        percentage={true}
        primaryMinSize={minMosaicPanelSize}
        secondaryMinSize={minMosaicPanelSize}
        secondaryInitialSize={percent}
        customClassName={classNames("splitter-layout", "SplitterStyles")}
        onDragEnd={onSplitterDragEnd(path)}
        onSecondaryPaneSizeChange={updatePanelSize(path)}
      >
        {renderWindows(node.first, path.concat("first"), onSplitterDragEnd, updatePanelSize)}
        {renderWindows(node.second, path.concat("second"), onSplitterDragEnd, updatePanelSize)}
      </SplitterLayout>
    );
  }
}

export default function MosaicManager(): JSX.Element {
  const dispatch = useDispatch();
  const currentNode = useSelector((state: IRootState) => state.windowManager.currentNode);
  const [secondaryPanelSize, setSecondaryPanelSize] = useState<IPanelSizes>({ root: 50 });

  const updatePanelSize = (path: string[]) => (value: number) => {
    const key = path.length > 0 ? path.join("-") : "root";
    const newPabekSize = { ...secondaryPanelSize };
    newPabekSize[key] = value;
    setSecondaryPanelSize(newPabekSize);
  };

  const onSplitterDragEnd = (path: string[]) => () => {
    const key = path.length > 0 ? path.join("-") : "root";
    dispatch(MosaicManagerActions.updateSplitViewPercentage(secondaryPanelSize[key], path));
  };

  useLayoutEffect(() => {
    function updateSize(): void {
      dispatch(MosaicManagerActions.updateSplitViewPercentage(secondaryPanelSize["root"], []));
    }
    window.addEventListener("resize", updateSize);
    let ignore = false;
    if (!ignore) {
      updateSize();
    }
    return () => {
      ignore = true;
      window.removeEventListener("resize", updateSize);
    };
  }, [dispatch, secondaryPanelSize]);

  return (
    <Grid container direction={"column"} sx={{ width: "100%", display: "flex", flexGrow: 1 }}>
      {renderWindows(currentNode, [], onSplitterDragEnd, updatePanelSize)}
      {renderPanels(currentNode, [])}
    </Grid>
  );
}
